import {
  initializeAnalytics,
  getInitialAnalyticsProperties,
  SERVER_PURPOSE_LIMITATION_NAMES,
} from '@nike/privacy-shared-logic';
import type { PurposeLimitationCategory } from '@nike/privacy-types';

import { ANALYTICS_EVENTS, ANALYTICS_DETAILS } from '../constants';

const getAnalyticsProperties = () =>
  getInitialAnalyticsProperties({
    pageName: ANALYTICS_DETAILS.pageName,
    viewPageType: ANALYTICS_DETAILS.pageType,
  });
const getPageViewedFunction = () => initializeAnalytics(ANALYTICS_DETAILS.experience).pageViewed;
const getTrackClickFunction = () => initializeAnalytics(ANALYTICS_DETAILS.experience).trackClick;

/**
 * @name analyticsTrackPageViewedCookieDialog
 * @description To call page viewed analytics event when CookieDialog has been rendered
 * @returns {void}
 */
export const analyticsTrackPageViewedCookieDialog = (): void => {
  const analyticsFunction = getPageViewedFunction();

  analyticsFunction(
    ANALYTICS_DETAILS.pageName,
    ANALYTICS_EVENTS.PRIVACY_MODAL_VIEWED.name,
    getAnalyticsProperties(),
  );
};

/**
 * @name analyticsTrackPageViewedError
 * @description To call page viewed analytics event when InlineError has been rendered
 * @returns {void}
 */
export const analyticsTrackPageViewedError = (): void => {
  const analyticsFunction = getPageViewedFunction();

  analyticsFunction(
    ANALYTICS_DETAILS.pageName,
    ANALYTICS_EVENTS.PRIVACY_MODAL_ERROR_VIEWED.name,
    getAnalyticsProperties(),
  );
};

/**
 * @name analyticsTrackClickAcceptAll
 * @description To call track click analytics event when accept all / decline all button clicked to fire event for purpose limitations
 * @param {string} purposeLimitationsCategories - A list of visible pls a user purpose limitations category
 * @param {boolean} isAcceptAll - indicates whether a user clicked on Accept All or Decline All
 * @returns {void}
 */
const analyticsTrackClickAll = (
  purposeLimitationsCategories: PurposeLimitationCategory[],
  isAcceptAll: boolean,
) => {
  purposeLimitationsCategories.forEach(purposeLimitationsCategory => {
    switch (purposeLimitationsCategory) {
      case SERVER_PURPOSE_LIMITATION_NAMES.TARGETING_BEHAVIORAL_EVENTS: {
        const analyticsEvent = isAcceptAll
          ? ANALYTICS_EVENTS.PRIVACY_MODAL_BEHAVIORAL_ADVERTISING_ENABLED_CLICKED
          : ANALYTICS_EVENTS.PRIVACY_MODAL_BEHAVIORAL_ADVERTISING_DISABLED_CLICKED;
        const analyticsFunction = getTrackClickFunction();

        const eventOptions = {
          ...getAnalyticsProperties(),
          clickActivity: analyticsEvent.clickActivity,
        };

        analyticsFunction(analyticsEvent.name, eventOptions);
        break;
      }
      case SERVER_PURPOSE_LIMITATION_NAMES.PERFORMANCE: {
        const analyticsEvent = isAcceptAll
          ? ANALYTICS_EVENTS.PRIVACY_MODAL_PERFORMANCE_AND_ANALYTICS_ENABLED_CLICKED
          : ANALYTICS_EVENTS.PRIVACY_MODAL_PERFORMANCE_AND_ANALYTICS_DISABLED_CLICKED;
        const analyticsFunction = getTrackClickFunction();

        const eventOptions = {
          ...getAnalyticsProperties(),
          clickActivity: analyticsEvent.clickActivity,
        };

        analyticsFunction(analyticsEvent.name, eventOptions);
        break;
      }
    }
  });
};

/**
 * @name analyticsTrackClickAcceptAll
 * @description To call track click analytics event when accept all button clicked
 * @param {string} purposeLimitationsCategories - A list of visible pls a user purpose limitations category
 * @returns {void}
 */
export const analyticsTrackClickAcceptAll = (
  purposeLimitationsCategories: PurposeLimitationCategory[],
): void => {
  const analyticsEvent = ANALYTICS_EVENTS.PRIVACY_MODAL_ACCEPT_ALL_CLICKED;
  const analyticsFunction = getTrackClickFunction();

  const eventOptions = {
    ...getAnalyticsProperties(),
    clickActivity: analyticsEvent.clickActivity,
  };

  analyticsFunction(analyticsEvent.name, eventOptions);

  analyticsTrackClickAll(purposeLimitationsCategories, true);
};

/**
 * @name analyticsTrackClickDeclineAll
 * @description To call track click analytics event when decline all button clicked
 * @param {string} purposeLimitationsCategories - A list of visible pls a user purpose limitations category
 * @returns {void}
 */
export const analyticsTrackClickDeclineAll = (
  purposeLimitationsCategories: PurposeLimitationCategory[],
): void => {
  const analyticsEvent = ANALYTICS_EVENTS.PRIVACY_MODAL_DECLINE_ALL_CLICKED;
  const analyticsFunction = getTrackClickFunction();

  const eventOptions = {
    ...getAnalyticsProperties(),
    clickActivity: analyticsEvent.clickActivity,
  };

  analyticsFunction(analyticsEvent.name, eventOptions);

  analyticsTrackClickAll(purposeLimitationsCategories, false);
};
